import React, { useRef } from 'react';
import styled from '@emotion/styled';
import { Flex } from '$components/layouts';
import { IProduct, IReview } from '$models';
import { BOG_NU_URL } from '~/lib/helpers';
import { useTranslation } from '$hooks';
import { mq } from '$lib/helpers';
import { IconReview } from '$components/elements/icon-review';
import { Button } from '$components/elements/button';
import { Collapsible } from '$components/elements/collapsible';
import { UserReview } from './components/user-review';
import { MediaReview } from './components/media-review';
import { Link } from '$components/elements/link';

type ReviewsProps = {
    reviews: IReview;
    product?: IProduct;
    onOpenReviewModal?: () => void;
    readOnly?: boolean;
};

export const Reviews = (props: ReviewsProps) => {
    const { reviews, product, onOpenReviewModal } = props;
    const { translate } = useTranslation();
    const containerRef = useRef<HTMLDivElement>(null);

    const calculatedUserScore = ((reviews?.userReviewsAverageScore || 0) / 100) * 5;
    const calculatedMediaScore = ((reviews?.mediaReviewAverageScore || 0) / 100) * 5;

    const fixedReview = (score: number) => {
        return score % 1 === 0 ? score.toFixed(0) : score.toFixed(1);
    };

    const renderReviewSectionSummary = (score: string, numberOfReviews: number) => (
        <ReviewSectionSummary>
            {`${translate('productPage.productReviews.teaser1')} `}
            <span>{score}</span>
            {` ${translate('productPage.productReviews.teaser2')} `}
            <span>5</span>
            {` ${translate('productPage.productReviews.teaser3')} `}
            <span>{numberOfReviews} </span>
            {numberOfReviews === 1
                ? translate('productPage.productReviews.teaser4Single')
                : translate('productPage.productReviews.teaser4')}
        </ReviewSectionSummary>
    );

    return (
        <Container ref={containerRef}>
            <ReviewSection column>
                <ReviewHeaderContainer>
                    <ReviewSectionHeader>{translate('productPage.productReviews.userReviews')}</ReviewSectionHeader>
                    <Flex alignItems={'center'} gap={'small'}>
                        <IconReview score={calculatedUserScore} />
                        {renderReviewSectionSummary(
                            fixedReview(calculatedUserScore),
                            reviews?.userReviews?.length || 0
                        )}
                    </Flex>
                </ReviewHeaderContainer>
                <StyledCollapsible
                    collapseCTAText={translate('productPage.productReviews.readLessReviews')}
                    collapsedHeight={250}
                    expandCTAText={translate('productPage.productReviews.readAllReviews')}
                >
                    {reviews?.userReviews?.map((review) => (
                        <UserReview key={review.id} review={review} />
                    ))}
                </StyledCollapsible>
                {!props.readOnly && (
                    <Flex justifyContent="center">
                        <Button variant="primary" onClick={onOpenReviewModal}>
                            {translate('productPage.productReviews.writeUserReview')}
                        </Button>
                    </Flex>
                )}
            </ReviewSection>
            {product?.isBook && (
                <ReviewSection column>
                    <ReviewHeaderContainer>
                        <ReviewSectionHeader>{translate('productPage.productReviews.reviews')}</ReviewSectionHeader>
                        <Flex alignItems="center" gap="small">
                            <IconReview score={calculatedMediaScore} />
                            {renderReviewSectionSummary(
                                fixedReview(calculatedMediaScore),
                                reviews?.bogNuReviews?.length || 0
                            )}
                        </Flex>
                    </ReviewHeaderContainer>
                    <Collapsible
                        collapseCTAText={translate('productPage.productReviews.readLessReviews')}
                        collapsedHeight={280}
                        expandCTAText={translate('productPage.productReviews.readAllReviews')}
                    >
                        {reviews?.bogNuReviews?.map((review) => (
                            <MediaReview key={review.bogNuReviewId} review={review} />
                        ))}
                    </Collapsible>
                    {!props.readOnly && (
                        <BookDetailsLink
                            themedLink
                            href={`${BOG_NU_URL}${product.itemIsbn}`}
                            rel="nofollow"
                            target="_blank"
                        >
                            {translate('productPage.productReviews.bogNuLabel')}
                        </BookDetailsLink>
                    )}
                </ReviewSection>
            )}
        </Container>
    );
};

const Container = styled.div({
    width: '100%',
});

const ReviewSection = styled(Flex)(({ theme }) => ({
    marginBottom: theme.space[9],
}));

const ReviewSectionHeader = styled.h6(({ theme }) => ({
    marginBottom: theme.space[3],
    fontFamily: theme.fontFamilies.regular,
    fontWeight: theme.fontWeights.semiBold,
}));

const ReviewSectionSummary = styled.span(({ theme }) => ({
    paddingLeft: theme.space[1],
    fontSize: theme.fontSizes.sm,
    '& span': {
        fontSize: theme.fontSizes.sm,
        fontWeight: theme.fontWeights.bold,
    },
}));

const ReviewHeaderContainer = styled.div(({ theme }) => ({
    borderBottom: `1px solid ${theme.colors.primaryGreyLight}`,
    paddingBottom: `${theme.space[3]}`,
    marginBottom: `${theme.space[3]}`,
}));

const BookDetailsLink = styled(Link)(({ theme }) => ({
    marginTop: `${theme.space[11]}`,
    fontSize: theme.fontSizes.sm,
}));

const StyledCollapsible = styled(Collapsible)(({ theme: { space } }) => ({
    [mq(0, 'md')]: {
        marginBottom: space[4],
    },
}));
